import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { useForm } from 'react-hook-form';

import { useBlockHoursDayRange } from '@/shared/actions/erp';
import { CustomTextArea, SingleButton } from '@/shared/components/ui';
import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import { AlertSeverityEnum } from '@/shared/interfaces';
import { permaBlockTimeSchema } from '@/shared/utils/validation-schema';
import { useAuthStore } from '@/store/auth';
import { usePlanificadorStore } from '@/store/erp';
import { useUiStore } from '@/store/ui';
import RangePickerPlanificador from './RangePickerPlanificador';
import { PermanentBlockTimeForm } from '@/shared/interfaces/erp/mantenimiento-operaciones';
import { ROUTER_PATHS } from '@/shared';

export type CustomBlockTimeModalProps = {
  planificadorId: string;
};

type PermaBlockForm = Pick<PermanentBlockTimeForm, 'motivo'> & {
  isUnblockRequest?: boolean;
};

const CustomBlockTimeModal: React.FC<CustomBlockTimeModalProps> = ({
  planificadorId,
}) => {
  const flatpickrRef = useRef<Flatpickr | null>(null);
  const returnUrlPlanificadoresPage =
    ROUTER_PATHS.mantenimientoOperaciones.planificadorNav;
  ///* local state ----------------
  const [rangeDate, setRangeDate] = useState<Date[]>([]);

  ///* global state ----------------
  const isOpenCustomBlockTime = usePlanificadorStore(
    s => s.isOpenCustomBlockTime
  );
  const setIsOpenCustomBlockTime = usePlanificadorStore(
    s => s.setIsOpenCustomBlockTime
  );
  const handleRefreshPlanificador = usePlanificadorStore(
    s => s.handleRefreshPlanificador
  );
  const mondayToSunday = usePlanificadorStore(s => s.mondayToSunday);
  const user_id = useAuthStore(s => s.user?.user_id);
  const setAlert = useUiStore(s => s.setAlert);

  const selectedPlanificador = usePlanificadorStore(
    s => s.selectedPlanificador
  );

  ///* form -----------------
  const form = useForm<PermaBlockForm>({
    resolver: yupResolver(permaBlockTimeSchema),
    defaultValues: {
      isUnblockRequest: true,
    },
  });
  const { formState } = form;

  const customErrorCallback = () => {
    //window.location.href = `${returnUrlPlanificadoresPage}/horario/${planificadorId}`;
  };

  ///* mutations ----------------
  const blockHoursDayRange = useBlockHoursDayRange({
    enableRedirect: false,
    customErrorCallback,
  });

  ///* handlers ----------------
  const onSubmit = async (data: PermaBlockForm) => {
    if (!rangeDate.length || rangeDate.length < 2) {
      setAlert(
        true,
        'El rango de fechas no puede estar vacío',
        AlertSeverityEnum.error
      );

      return;
    }

    setIsOpenCustomBlockTime(false);

    const resp = await blockHoursDayRange.mutateAsync({
      motivo: data.motivo,
      fecha_inicio: dayjs(rangeDate[0]).format('YYYY-MM-DD'),
      fecha_fin: dayjs(rangeDate[1]).format('YYYY-MM-DD'),
      user_id: user_id!,
      flota: selectedPlanificador?.flota_data?.codigo!,
    });

    handleClose();

    console.log(resp.data.at(0)?.time_map!, 'block');

    handleRefreshPlanificador(resp.data.at(0)?.time_map!);
  };

  const handleClose = () => {
    setIsOpenCustomBlockTime(false);
    form.reset();
    form.setValue('motivo', '');
  };

  const onChangeRange = (days: Date[]) => {
    setRangeDate(days);
  };

  return (
    <>
      <ScrollableDialogProps
        open={isOpenCustomBlockTime}
        title={'Bloquear Horario'}
        minWidth="30%"
        onClose={handleClose}
        // custom action buttons ------
        showCustomButtons
        customButtons={
          <>
            <SingleButton
              label="Cancelar"
              onClick={handleClose}
              variant="text"
              disabled={formState.isSubmitting}
            />

            <SingleButton
              label="Bloquear"
              onClick={form.handleSubmit(onSubmit, err => {
                console.log(err);
              })}
              variant="outlined"
              disabled={formState.isSubmitting}
            />
          </>
        }
        // content ------
        contentNode={
          <Grid item xs={12} container spacing={3} pt={2} pb={3}>
            <Grid item xs={12}>
              <Typography variant="h6">
                Selecciona los días a bloquear:
              </Typography>
            </Grid>

            <RangePickerPlanificador
              flatpickrRef={flatpickrRef}
              onChange={onChangeRange}
              minDate={dayjs(mondayToSunday[0]).toDate()}
              maxDate={dayjs(mondayToSunday[1]).toDate()}
            />

            <Grid item xs={12}>
              <CustomTextArea
                label="Motivo"
                name="motivo"
                required
                rows={4}
                control={form.control}
                defaultValue={form.getValues().motivo}
                error={formState.errors.motivo}
                helperText={formState.errors.motivo?.message}
              />
            </Grid>
          </Grid>
        }
      />
    </>
  );
};

export default CustomBlockTimeModal;
